'use client';

import Script from 'next/script';
import { useCurrentUser } from '@/providers/CurrentUserProvider';
import { useEffect, useState } from 'react';

/**
 * Google Analytics component for Next.js
 * 
 * Features:
 * - Only loads in production (when NEXT_PUBLIC_GA_ID is set)
 * - Excludes super admin traffic from analytics
 * - Uses Next.js Script component for optimal loading
 * 
 * @see /docs/GOOGLE_ANALYTICS_SETUP.md for setup instructions
 */
export default function GoogleAnalytics() {
  const { user, userLoading } = useCurrentUser();
  const [shouldLoad, setShouldLoad] = useState(false);
  const gaId = process.env.NEXT_PUBLIC_GA_ID;

  useEffect(() => {
    // Don't load if no GA ID is configured
    if (!gaId) {
      return;
    }

    // Wait for user data to load
    if (userLoading) {
      return;
    }

    // Exclude super admins from analytics
    if (user?.role === 'SUPER_ADMIN') {
      setShouldLoad(false);
      return;
    }

    // Check for cookie consent (GDPR compliance)
    const consent = typeof window !== 'undefined' ? localStorage.getItem('cookie-consent') : null;
    
    if (!consent) {
      // User hasn't made a choice yet - don't load
      setShouldLoad(false);
      return;
    }

    if (consent === 'rejected') {
      // User rejected analytics cookies
      setShouldLoad(false);
      return;
    }

    if (consent === 'accepted') {
      // Load analytics for consenting users
      setShouldLoad(true);
    }
  }, [user, userLoading, gaId]);

  // Don't render anything if GA ID is not configured or if super admin
  if (!gaId || !shouldLoad) {
    return null;
  }

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${gaId}', {
            page_path: window.location.pathname,
          });
        `}
      </Script>
    </>
  );
}
